import { Link } from 'react-router-dom';

const SchoolsSection = () => {
  const schoolDistricts = [
    {
      name: 'Roseville City School District',
      grades: 'K-8',
      schools: 18,
      rating: 8.2,
      highlights: ['California Distinguished Schools', 'STEM Programs', 'Arts Integration'],
      website: 'https://www.rcsdk8.org'
    },
    {
      name: 'Roseville Joint Union High School District',
      grades: '9-12',
      schools: 6,
      rating: 8.5,
      highlights: ['Blue Ribbon Schools', 'AP & IB Programs', 'Championship Athletics'],
      website: 'https://www.rjuhsd.us'
    },
    {
      name: 'Dry Creek Joint Elementary School District',
      grades: 'K-8',
      schools: 8,
      rating: 8.3,
      highlights: ['Technology Integration', 'Award-Winning Programs', 'Community Focus'],
      website: 'https://www.dcsdk12.org'
    }
  ];

  const topSchools = [
    {
      name: 'Granite Bay High School',
      type: 'High School',
      rating: 10,
      image: 'https://public.readdy.ai/ai/img_res/d597569883fee2c9eb5c754812f2a628.jpg'
    },
    {
      name: 'Woodcreek High School',
      type: 'High School',
      rating: 9,
      image: 'https://readdy.ai/api/search-image?query=contemporary%20high%20school%20building%20with%20students%20walking%20sunny%20day%20California%20modern%20design%20landscaped%20grounds%20blue%20sky&width=400&height=300&seq=whs2&orientation=landscape'
    },
    {
      name: 'Eich Middle School',
      type: 'Middle School',
      rating: 9,
      image: 'https://readdy.ai/api/search-image?query=modern%20middle%20school%20campus%20with%20students%20outdoors%20sunny%20California%20day%20contemporary%20buildings%20open%20spaces%20blue%20sky&width=400&height=300&seq=ems2&orientation=landscape'
    },
    {
      name: 'Woodcreek Oaks Elementary',
      type: 'Elementary',
      rating: 9,
      image: 'https://readdy.ai/api/search-image?query=welcoming%20elementary%20school%20with%20children%20playing%20sunny%20California%20day%20colorful%20playground%20modern%20facilities%20blue%20sky&width=400&height=300&seq=woes2&orientation=landscape'
    }
  ];

  return (
    <section id="schools" className="py-20 px-4 bg-white">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-indigo-100 text-indigo-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
            <i className="ri-graduation-cap-line"></i>
            Education Excellence
          </div>
          <h2 className="text-4xl font-bold text-gray-900 mb-4">Schools &amp; Education</h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Roseville is home to top-rated schools and excellent educational opportunities for students of all ages
          </p>
        </div>

        {/* School Districts */}
        <div className="mb-16">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">School Districts</h3>
          <div className="grid md:grid-cols-3 gap-6">
            {schoolDistricts.map((district, index) => (
              <div key={index} className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl p-6 border-2 border-indigo-100">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h4 className="text-lg font-bold text-gray-900 mb-1">{district.name}</h4>
                    <div className="text-sm text-gray-600">Grades {district.grades}</div>
                  </div>
                  <div className="bg-white rounded-lg px-3 py-1 shadow-sm">
                    <div className="flex items-center gap-1">
                      <i className="ri-star-fill text-yellow-500 text-sm"></i>
                      <span className="font-bold text-gray-900">{district.rating}</span>
                    </div>
                  </div>
                </div>

                <div className="mb-4">
                  <div className="text-2xl font-bold text-indigo-600 mb-1">{district.schools}</div>
                  <div className="text-sm text-gray-600">Schools in District</div>
                </div>

                <div className="mb-4">
                  <div className="text-sm font-semibold text-gray-700 mb-2">Highlights</div>
                  <ul className="space-y-1">
                    {district.highlights.map((highlight, idx) => (
                      <li key={idx} className="text-sm text-gray-600 flex items-start gap-2">
                        <i className="ri-check-line text-indigo-600 mt-0.5"></i>
                        <span>{highlight}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <a
                  href={district.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block text-center bg-indigo-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-indigo-700 transition-colors whitespace-nowrap"
                >
                  <i className="ri-external-link-line mr-2"></i>
                  Visit Website
                </a>
              </div>
            ))}
          </div>
        </div>

        {/* Top-Rated Schools */}
        <div className="mb-12">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-2xl font-bold text-gray-900">Top-Rated Schools</h3>
            <Link
              to="/schools"
              className="text-indigo-600 hover:text-indigo-700 font-semibold flex items-center gap-2 whitespace-nowrap"
            >
              View All Schools
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {topSchools.map((school, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <div className="relative h-48">
                  <img
                    src={school.image}
                    alt={school.name}
                    className="w-full h-full object-cover object-top"
                  />
                  <div className="absolute top-3 right-3 bg-white rounded-full px-3 py-1 shadow-lg">
                    <div className="flex items-center gap-1">
                      <i className="ri-star-fill text-yellow-500 text-sm"></i>
                      <span className="font-bold text-gray-900 text-sm">{school.rating}/10</span>
                    </div>
                  </div>
                </div>
                <div className="p-4">
                  <h4 className="font-bold text-gray-900 mb-1">{school.name}</h4>
                  <p className="text-sm text-gray-600">{school.type}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Education Stats */}
        <div className="bg-gradient-to-br from-indigo-600 to-purple-600 rounded-2xl p-8 text-white">
          <h3 className="text-2xl font-bold mb-6 text-center">Education by the Numbers</h3>
          <div className="grid md:grid-cols-4 gap-6 text-center">
            <div>
              <div className="text-4xl font-bold mb-2">32</div>
              <div className="text-sm opacity-90">Total Schools</div>
            </div>
            <div>
              <div className="text-4xl font-bold mb-2">8.3</div>
              <div className="text-sm opacity-90">Average Rating</div>
            </div>
            <div>
              <div className="text-4xl font-bold mb-2">22:1</div>
              <div className="text-sm opacity-90">Student-Teacher Ratio</div>
            </div>
            <div>
              <div className="text-4xl font-bold mb-2">95%</div>
              <div className="text-sm opacity-90">Graduation Rate</div>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-12 text-center">
          <Link
            to="/schools"
            className="inline-flex items-center gap-2 bg-indigo-600 text-white px-8 py-4 rounded-xl font-bold text-lg hover:bg-indigo-700 transition-colors shadow-lg whitespace-nowrap"
          >
            <i className="ri-graduation-cap-line text-2xl"></i>
            Explore All Schools
          </Link>
        </div>
      </div>
    </section>
  );
};

export default SchoolsSection;